/**
 * Per-group Zustand store for Study Groups.
 *
 * One store, keyed by `groupId`. The alternative would be a Zustand
 * store per group, which pollutes the devtools timeline and requires
 * each `useSyncExternalStore` caller to know which store to read from.
 * Keeping it all in one store lets components pick their own slice
 * via `useStudyGroupStore(s => s.groups[groupId]?.meta)` with no
 * framework ceremony.
 *
 * Populated by `useStudyGroupDoSync(groupId)`:
 *   - WS connects → `setConnectionStatus(groupId, "connected")`
 *   - sg-sync-diff arrives → all slice setters fire
 *   - sg-*-updated broadcast arrives → per-entity setters fire
 *   - Local mutations → per-entity setters + side-effect push to WS
 *
 * Read by `sync.studyGroups.group(id).meta.use()` and friends in
 * `src/sync/study-groups.ts`.
 */

import type {
  SGActivityEntry,
  SGDeckMindmapEntry,
  SGDeckProps,
  SGDrawSettings,
  SGFolder,
  SGMember,
  StudyGroupMeta,
} from '@flashka/shared/sync-engine/schema/group-entities';
import type { SyncCardProps } from '@flashka/shared/sync-engine/schema/shared-types';

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { mergeDeckMetaEntry } from '../../../sync-engine/framework/deck-materials-merge';
import { SG_DECK_CARDS_TABLE } from '../../../sync-engine/framework/local-tables';
import { getSyncStorage, type StoredSGDeckCards } from '../../provider/sync-storage';
import type { LiveScoreCache } from '../../study-groups/leaderboard';
import { safeBulkPut } from './store-utils';

// Local row shape for the persisted snapshot table. Mirrors the Dexie schema
// in src/database/db.ts but lives here so this store does not depend on the
// web-only db module — RN can implement the same SyncStorage table.
//
// Since the persistence split the snapshot carries everything EXCEPT `cards`
// (written as `{}`); a group's card bodies live in `SG_DECK_CARDS_TABLE`, one
// row per deck. A snapshot written before the split still carries inline
// cards — `hydrateGroupFromStorage` reads them once, and the first persist
// after that rewrites the snapshot without them.
type SGSnapshotRow = {
  groupId: string;
  slice: unknown;
  savedAt: string;
};

const SG_SNAPSHOTS_TABLE = 'sgGroupSnapshots';

/** One deck's card map with `cards` merged in under per-card LWW. */
function mergeCardMap(
  existingDeck: Record<string, SyncCardProps> | undefined,
  cards: readonly SyncCardProps[],
): Record<string, SyncCardProps> {
  const next = { ...(existingDeck ?? {}) };
  for (const c of cards) {
    const prev = next[c.id];
    // Per-card LWW — keep the newer one.
    if (!prev || (c.updatedAtISO ?? '') >= (prev.updatedAtISO ?? '')) {
      next[c.id] = c;
    }
  }
  return next;
}

/**
 * A hidden-source deck hands a member NOTHING of its documents: the group
 * sends every viewer but the deck's owner the row WITHOUT `sources`
 * (`withholdHiddenSource` on the server), meaning "there are none for you",
 * not "unchanged". The row merge reads an absent list as "keep the other
 * side", so a member who held the documents from before the owner hid them
 * would keep them — and hand them on through Import / Copy to personal
 * library. Drop them from this device's copy before merging; the owner's
 * devices (who can hold an unsent attach of their own) merge as usual.
 * Applies only to rows the server sent (`viewer` given) — a local write
 * carries whatever this device already holds.
 */
function withoutWithheldSources(
  prev: SGDeckProps,
  incoming: SGDeckProps,
  viewer: SgRowViewer | undefined,
): SGDeckProps {
  const local = prev as SGDeckProps & { sources?: unknown };
  if (
    viewer === undefined ||
    incoming.hideSource !== true ||
    (incoming as { sources?: unknown }).sources !== undefined ||
    local.sources === undefined ||
    incoming.addedByUserId === viewer.userId
  ) {
    return prev;
  }
  const { sources: _withheld, ...rest } = local;
  return rest as SGDeckProps;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Who a server-sent deck-props row was addressed to (`mergeDeckProps`). */
export type SgRowViewer = { userId: string | null };

export type SGConnectionStatus =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'error'
  | 'kicked';

/**
 * Per-group slice. Kept flat-ish so selectors don't have to dig two
 * levels deep. `cards` is keyed by `{deckId → cardId → CardProps}`
 * for O(1) per-card reads; the dashboard tile doesn't need it at all
 * (meta + counts suffice).
 */
/**
 * A removal of a group deck this device sent and the group has not answered
 * yet (see `study-groups/deck-removal.ts`). Kept IN the slice so it is
 * persisted with the snapshot: an app killed offline re-sends it on the next
 * sync instead of forgetting it.
 */
export type PendingDeckRemoval = {
  deletedTimeISO: string;
  /** Who removed it (the sharer) — the wire's `userId`. */
  userId: string;
  /** The newest copy of the row, put back if the group refuses. */
  row?: SGDeckProps;
};

export type StudyGroupSlice = {
  groupId: string;
  meta?: StudyGroupMeta;
  /** Who may draw on the group's PDF sources. Absent = everyone. */
  drawSettings?: SGDrawSettings;
  members: Record<string, SGMember>;
  folders: Record<string, SGFolder>;
  deckProps: Record<string, SGDeckProps>;
  cards: Record<string, Record<string, SyncCardProps>>;
  activity: Record<string, SGActivityEntry>;
  /** Shared decks' mind maps, by `${sgDeckId}\x01${mindmapId}`. Optional:
   *  a snapshot saved before maps were shared has none. */
  deckMindmaps?: Record<string, SGDeckMindmapEntry>;
  connectionStatus: SGConnectionStatus;
  /** True once the group's Yjs doc has loaded from IndexedDB OR connected
   *  to the server. Mirrors `UserNotesDocContext.synced` for the user doc;
   *  notes editors gate rendering on this when viewing a shared deck. */
  notesSynced: boolean;
  /** Last-applied timestamps keyed by the entity's tsKey. */
  serverTimestamps: Record<string, string>;
  /**
   * This device's unanswered deck removals, by deck id. While one is pending
   * the deck stays hidden here — a re-sync that re-ships the row does not
   * bring it back; only the group's refusal does.
   */
  pendingRemovals?: Record<string, PendingDeckRemoval>;
  /**
   * Last `sg-score-ack` scalars from the server — populated by the
   * study-group-do-adapter's inbound case, read by
   * `sgGroup(id).leaderboard.liveScore()`. `undefined` until the first
   * ack arrives. Centi-MP integers; UI consumers convert to display MP.
   */
  liveScore?: LiveScoreCache;
  /**
   * Decks whose COMPLETE shared card set is in `cards` — the SG twin of the
   * personal store's `loadedDeckIds`. Set by the server's `sg-decks-loaded`
   * completion frame (after a sync-init stream, a peer's deck push, an
   * on-demand load), by a whole-deck local write (`setDeckCards`) and by
   * hydrate for every deck that has a row on this device; dropped with the
   * deck. A card frame alone never sets it: a stream's frames are partial
   * by construction. In memory only — it is DERIVED on disk: a deck's card
   * row is written only once the deck is loaded, so "has a row" ⇔ "was
   * complete when written" (see `persistGroup`). Loaded ⇒ `cards[deckId]`
   * exists (an empty bucket for a deck with no shared card — `markDecksLoaded`
   * guarantees it), so a complete-but-empty deck has a row too. Cleared again
   * by `markDecksUnloaded` while a stream re-ships the deck, and dropped with
   * the deck (`removeDeck`, a tombstone in `mergeDeckProps`).
   */
  loadedDeckIds: ReadonlySet<string>;
};

function emptySlice(groupId: string): StudyGroupSlice {
  return {
    groupId,
    members: {},
    folders: {},
    deckProps: {},
    cards: {},
    activity: {},
    connectionStatus: 'disconnected',
    notesSynced: false,
    serverTimestamps: {},
    loadedDeckIds: new Set(),
  };
}

/** `loaded` plus `deckIds` — the same set when nothing is new. */
function withLoaded(
  loaded: ReadonlySet<string>,
  deckIds: readonly string[],
): ReadonlySet<string> {
  if (deckIds.every((id) => loaded.has(id))) return loaded;
  const next = new Set(loaded);
  for (const id of deckIds) next.add(id);
  return next;
}

export type StudyGroupStoreState = {
  groups: Record<string, StudyGroupSlice>;

  // ── Lifecycle ──────────────────────────────────────────────────────
  ensureGroup: (groupId: string) => void;
  removeGroup: (groupId: string) => void;
  resetAll: () => void;

  // ── Offline persistence ───────────────────────────────────────────
  /** Populate this group's slice from IndexedDB (cold-start rehydrate). */
  hydrateGroupFromStorage: (groupId: string) => Promise<void>;
  /** Replace whole slice from storage (used internally by hydrate). */
  replaceGroup: (groupId: string, slice: StudyGroupSlice) => void;

  // ── Connection ────────────────────────────────────────────────────
  setConnectionStatus: (groupId: string, status: SGConnectionStatus) => void;
  setNotesSynced: (groupId: string, synced: boolean) => void;

  // ── Entity setters (called by the sync hook on broadcasts + diffs)
  setMeta: (groupId: string, meta: StudyGroupMeta | undefined) => void;
  setDrawSettings: (groupId: string, settings: SGDrawSettings | undefined) => void;
  setMembers: (groupId: string, members: SGMember[]) => void;
  mergeMembers: (groupId: string, members: SGMember[]) => void;
  setFolders: (groupId: string, folders: SGFolder[]) => void;
  mergeFolders: (groupId: string, folders: SGFolder[]) => void;
  setDeckProps: (groupId: string, props: SGDeckProps[]) => void;
  /**
   * Fold rows into the group's deck props (row LWW + the materials/documents
   * union). Pass `viewer` for rows the GROUP SERVER sent this device, so a
   * hidden-source row's withheld documents are dropped (see
   * `withoutWithheldSources`); omit it for this device's own writes.
   */
  mergeDeckProps: (groupId: string, props: SGDeckProps[], viewer?: SgRowViewer) => void;
  /** Replace one deck's card map with its COMPLETE set (marks it loaded). */
  setDeckCards: (groupId: string, deckId: string, cards: SyncCardProps[]) => void;
  mergeCards: (groupId: string, deckId: string, cards: SyncCardProps[]) => void;
  /** `mergeCards` for several decks in ONE store tick — a card frame that
   *  spans decks costs its subscribers one notification, not one per deck. */
  mergeCardsBatch: (
    groupId: string,
    cardsByDeck: Record<string, SyncCardProps[]>,
  ) => void;
  /** The server said these decks' card sets are complete in `cards`. */
  markDecksLoaded: (groupId: string, deckIds: readonly string[]) => void;
  /**
   * The opposite flag flip: these decks' card sets are about to be, or were
   * left, INCOMPLETE — a sync-init stream re-ships a stale deck this client
   * held complete, and its bucket is partial from the first frame until the
   * completion frame (or for good, if the socket drops). The bucket stays in
   * memory (the frames merge over it, LWW); only the flag goes, so the deck's
   * row is dropped on the next persist and its timestamp leaves the manifest —
   * the server ships it whole again instead of skipping it on a partial set.
   */
  markDecksUnloaded: (groupId: string, deckIds: readonly string[]) => void;
  removeDeck: (groupId: string, deckId: string) => void;
  /** Record (or clear, with `null`) an unanswered removal of `deckId`. */
  setPendingDeckRemoval: (
    groupId: string,
    deckId: string,
    entry: PendingDeckRemoval | null,
  ) => void;
  upsertActivity: (groupId: string, entry: SGActivityEntry) => void;
  setActivity: (groupId: string, entries: SGActivityEntry[]) => void;
  upsertDeckMindmap: (groupId: string, entry: SGDeckMindmapEntry) => void;

  // ── Timestamp bookkeeping ─────────────────────────────────────────
  setServerTs: (groupId: string, tsKey: string, ts: string) => void;
  setServerTsBatch: (groupId: string, entries: Record<string, string>) => void;

  // ── Leaderboard live ack cache ────────────────────────────────────
  setLiveScore: (groupId: string, score: LiveScoreCache) => void;
};

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useStudyGroupStore = create<StudyGroupStoreState>()(
  devtools<StudyGroupStoreState>(
    (set) => ({
      groups: {},

      ensureGroup: (groupId) =>
        set(
          (s) =>
            s.groups[groupId]
              ? s
              : { groups: { ...s.groups, [groupId]: emptySlice(groupId) } },
          false,
          { type: 'sg/ensureGroup', groupId },
        ),

      hydrateGroupFromStorage: async (groupId) => {
        try {
          const current = useStudyGroupStore.getState().groups[groupId];
          // Only hydrate on true cold start. If the in-memory slice
          // already has any content (optimistic local writes, an
          // earlier sync-diff, or a prior hydrate in this session), a
          // full replace would CLOBBER those updates — in particular
          // a freshly-added deck racing with the 250ms write-through
          // debounce. Let the store stay as-is; the broadcast merge
          // path keeps it fresh.
          if (
            current &&
            (current.meta ||
              Object.keys(current.deckProps).length > 0 ||
              Object.keys(current.members).length > 0 ||
              Object.keys(current.folders).length > 0 ||
              Object.keys(current.cards).length > 0)
          ) {
            return;
          }
          const storage = getSyncStorage();
          const [row, deckRows] = await Promise.all([
            storage.getByKey<SGSnapshotRow>(SG_SNAPSHOTS_TABLE, groupId),
            storage.getByIndex<StoredSGDeckCards>(
              SG_DECK_CARDS_TABLE,
              'groupId',
              groupId,
            ),
          ]);
          if (!row) return;
          const slice = row.slice as StudyGroupSlice | undefined;
          if (!slice || slice.groupId !== groupId) return;
          // Cards: the snapshot's inline map (pre-split rows only; `{}` since)
          // overlaid by the per-deck rows, which are the current copy. Every
          // deck with cards on this device is COMPLETE: a row is written only
          // for a loaded deck, and a pre-split snapshot came from one whole
          // diff.
          const inlineCards = slice.cards ?? {};
          const legacyInline = Object.keys(inlineCards).length > 0;
          const cards: StudyGroupSlice['cards'] = { ...inlineCards };
          for (const deckRow of deckRows) {
            if (deckRow.groupId !== groupId) continue;
            cards[deckRow.deckId] = deckRow.cards;
          }
          const loadedDeckIds: ReadonlySet<string> = new Set(Object.keys(cards));
          // Re-check after the async IDB read — a local mutation may
          // have landed between the guard above and now.
          const latest = useStudyGroupStore.getState().groups[groupId];
          if (
            latest &&
            (latest.meta ||
              Object.keys(latest.deckProps).length > 0 ||
              Object.keys(latest.members).length > 0 ||
              Object.keys(latest.folders).length > 0 ||
              Object.keys(latest.cards).length > 0)
          ) {
            return;
          }
          const rehydrated: StudyGroupSlice = {
            ...emptySlice(groupId),
            ...slice,
            cards,
            loadedDeckIds,
            connectionStatus: 'disconnected',
          };
          // What is in memory now IS what is on disk (modulo the forced
          // connection status) — seed the persist diff with it so the first
          // change after boot writes only its own deck / the snapshot, not
          // the whole group again. EXCEPT after a pre-split snapshot: its
          // cards exist only inline, so the diff base is left empty and the
          // hydrate tick's own persist migrates them — snapshot rewritten
          // without cards, one row per deck — once.
          if (!legacyInline) lastPersistedRef.set(groupId, rehydrated);
          set(
            (s) => ({
              groups: { ...s.groups, [groupId]: rehydrated },
            }),
            false,
            { type: 'sg/hydrateFromStorage', groupId },
          );
        } catch (err) {
          console.error('[study-group-store] hydrateGroupFromStorage failed:', err);
        }
      },

      replaceGroup: (groupId, slice) =>
        set((s) => ({ groups: { ...s.groups, [groupId]: slice } }), false, {
          type: 'sg/replaceGroup',
          groupId,
        }),

      removeGroup: (groupId) =>
        set(
          (s) => {
            if (!s.groups[groupId]) return s;
            const { [groupId]: _dropped, ...rest } = s.groups;
            return { groups: rest };
          },
          false,
          { type: 'sg/removeGroup', groupId },
        ),

      resetAll: () => set({ groups: {} }, false, 'sg/resetAll'),

      setConnectionStatus: (groupId, status) =>
        set(
          (s) => {
            const existing = s.groups[groupId] ?? emptySlice(groupId);
            return {
              groups: {
                ...s.groups,
                [groupId]: { ...existing, connectionStatus: status },
              },
            };
          },
          false,
          { type: 'sg/setConnectionStatus', groupId, status },
        ),

      setNotesSynced: (groupId, synced) =>
        set(
          (s) => {
            const existing = s.groups[groupId] ?? emptySlice(groupId);
            if (existing.notesSynced === synced) return s;
            return {
              groups: {
                ...s.groups,
                [groupId]: { ...existing, notesSynced: synced },
              },
            };
          },
          false,
          { type: 'sg/setNotesSynced', groupId, synced },
        ),

      setMeta: (groupId, meta) =>
        set(
          (s) => {
            const existing = s.groups[groupId] ?? emptySlice(groupId);
            return {
              groups: {
                ...s.groups,
                [groupId]: { ...existing, meta },
              },
            };
          },
          false,
          { type: 'sg/setMeta', groupId },
        ),

      setDrawSettings: (groupId, drawSettings) =>
        set(
          (s) => {
            const existing = s.groups[groupId] ?? emptySlice(groupId);
            return {
              groups: {
                ...s.groups,
                [groupId]: { ...existing, drawSettings },
              },
            };
          },
          false,
          { type: 'sg/setDrawSettings', groupId },
        ),

      setMembers: (groupId, members) =>
        set(
          (s) => {
            const existing = s.groups[groupId] ?? emptySlice(groupId);
            const next: Record<string, SGMember> = {};
            for (const m of members) {
              if (!m.deletedTimeISO) next[m.userId] = m;
            }
            return {
              groups: { ...s.groups, [groupId]: { ...existing, members: next } },
            };
          },
          false,
          { type: 'sg/setMembers', groupId },
        ),

      mergeMembers: (groupId, members) =>
        set(
          (s) => {
            const existing = s.groups[groupId] ?? emptySlice(groupId);
            const next = { ...existing.members };
            for (const m of members) {
              if (m.deletedTimeISO) {
                delete next[m.userId];
                continue;
              }
              // Per-row LWW so a stale sync-diff doesn't clobber an
              // optimistic local update the server hasn't seen yet.
              const prev = next[m.userId];
              if (!prev || (m.updatedAtISO ?? '') >= (prev.updatedAtISO ?? '')) {
                next[m.userId] = m;
              }
            }
            return {
              groups: { ...s.groups, [groupId]: { ...existing, members: next } },
            };
          },
          false,
          { type: 'sg/mergeMembers', groupId },
        ),

      setFolders: (groupId, folders) =>
        set(
          (s) => {
            const existing = s.groups[groupId] ?? emptySlice(groupId);
            const next: Record<string, SGFolder> = {};
            for (const f of folders) {
              if (!f.deletedTimeISO) next[f.id] = f;
            }
            return {
              groups: { ...s.groups, [groupId]: { ...existing, folders: next } },
            };
          },
          false,
          { type: 'sg/setFolders', groupId },
        ),

      mergeFolders: (groupId, folders) =>
        set(
          (s) => {
            const existing = s.groups[groupId] ?? emptySlice(groupId);
            const next = { ...existing.folders };
            for (const f of folders) {
              if (f.deletedTimeISO) {
                delete next[f.id];
                continue;
              }
              const prev = next[f.id];
              if (!prev || (f.updatedAtISO ?? '') >= (prev.updatedAtISO ?? '')) {
                next[f.id] = f;
              }
            }
            return {
              groups: { ...s.groups, [groupId]: { ...existing, folders: next } },
            };
          },
          false,
          { type: 'sg/mergeFolders', groupId },
        ),

      setDeckProps: (groupId, props) =>
        set(
          (s) => {
            const existing = s.groups[groupId] ?? emptySlice(groupId);
            const next: Record<string, SGDeckProps> = {};
            for (const p of props) {
              if (p.deletedTimeISO || existing.pendingRemovals?.[p.id]) continue;
              next[p.id] = p;
            }
            return {
              groups: {
                ...s.groups,
                [groupId]: { ...existing, deckProps: next },
              },
            };
          },
          false,
          { type: 'sg/setDeckProps', groupId },
        ),

      mergeDeckProps: (groupId, props, viewer) =>
        set(
          (s) => {
            const existing = s.groups[groupId] ?? emptySlice(groupId);
            const next = { ...existing.deckProps };
            // A tombstone takes the deck's cards and its loaded flag with it —
            // exactly what `removeDeck` does on the legacy wire — so an unshared
            // deck's cards do not linger in memory, in the manifest, or (via the
            // next persist) on disk.
            let cards = existing.cards;
            let loadedDeckIds = existing.loadedDeckIds;
            let pendingRemovals = existing.pendingRemovals;
            for (const p of props) {
              const pendingRemoval = pendingRemovals?.[p.id];
              if (pendingRemoval && !p.deletedTimeISO) {
                // This device removed the deck and the group has not answered:
                // keep it hidden, but hold the newest copy in case of a refusal.
                const held = pendingRemoval.row;
                if (!held || (p.updatedAtISO ?? '') >= (held.updatedAtISO ?? '')) {
                  pendingRemovals = {
                    ...pendingRemovals,
                    [p.id]: { ...pendingRemoval, row: p },
                  };
                }
                continue;
              }
              if (p.deletedTimeISO) {
                delete next[p.id];
                if (p.id in cards) {
                  const { [p.id]: _dropped, ...rest } = cards;
                  cards = rest;
                }
                if (loadedDeckIds.has(p.id)) {
                  const set = new Set(loadedDeckIds);
                  set.delete(p.id);
                  loadedDeckIds = set;
                }
                continue;
              }
              const prev = next[p.id];
              // Row-level LWW (ties to incoming) — but the notes/maps index
              // is unioned per entry, the same merge the group DO runs on a
              // push: a server row that arrives newer than this device's
              // unsent edit must not un-list the note that edit created.
              next[p.id] = prev
                ? mergeDeckMetaEntry(withoutWithheldSources(prev, p, viewer), p)
                : p;
            }
            return {
              groups: {
                ...s.groups,
                [groupId]: {
                  ...existing,
                  deckProps: next,
                  cards,
                  loadedDeckIds,
                  ...(pendingRemovals ? { pendingRemovals } : {}),
                },
              },
            };
          },
          false,
          { type: 'sg/mergeDeckProps', groupId },
        ),

      setDeckCards: (groupId, deckId, cards) =>
        set(
          (s) => {
            const existing = s.groups[groupId] ?? emptySlice(groupId);
            const cardMap: Record<string, SyncCardProps> = {};
            for (const c of cards) cardMap[c.id] = c;
            return {
              groups: {
                ...s.groups,
                [groupId]: {
                  ...existing,
                  cards: { ...existing.cards, [deckId]: cardMap },
                  loadedDeckIds: withLoaded(existing.loadedDeckIds, [deckId]),
                },
              },
            };
          },
          false,
          { type: 'sg/setDeckCards', groupId, deckId },
        ),

      mergeCards: (groupId, deckId, cards) =>
        set(
          (s) => {
            const existing = s.groups[groupId] ?? emptySlice(groupId);
            return {
              groups: {
                ...s.groups,
                [groupId]: {
                  ...existing,
                  cards: {
                    ...existing.cards,
                    [deckId]: mergeCardMap(existing.cards[deckId], cards),
                  },
                },
              },
            };
          },
          false,
          { type: 'sg/mergeCards', groupId, deckId },
        ),

      mergeCardsBatch: (groupId, cardsByDeck) =>
        set(
          (s) => {
            const existing = s.groups[groupId] ?? emptySlice(groupId);
            const next = { ...existing.cards };
            for (const [deckId, cards] of Object.entries(cardsByDeck)) {
              next[deckId] = mergeCardMap(existing.cards[deckId], cards);
            }
            return {
              groups: { ...s.groups, [groupId]: { ...existing, cards: next } },
            };
          },
          false,
          { type: 'sg/mergeCardsBatch', groupId },
        ),

      markDecksLoaded: (groupId, deckIds) =>
        set(
          (s) => {
            const existing = s.groups[groupId] ?? emptySlice(groupId);
            const loadedDeckIds = withLoaded(existing.loadedDeckIds, deckIds);
            // Loaded ⇒ bucket: a complete deck with no shared card gets an empty
            // one, so it is persisted (a row) and hydrates as loaded like any
            // other complete deck.
            let cards = existing.cards;
            for (const id of deckIds) {
              if (!(id in cards)) cards = { ...cards, [id]: {} };
            }
            if (
              loadedDeckIds === existing.loadedDeckIds &&
              cards === existing.cards &&
              s.groups[groupId]
            ) {
              return s;
            }
            return {
              groups: { ...s.groups, [groupId]: { ...existing, cards, loadedDeckIds } },
            };
          },
          false,
          { type: 'sg/markDecksLoaded', groupId },
        ),

      markDecksUnloaded: (groupId, deckIds) =>
        set(
          (s) => {
            const existing = s.groups[groupId];
            if (!existing || !deckIds.some((id) => existing.loadedDeckIds.has(id)))
              return s;
            const loadedDeckIds = new Set(existing.loadedDeckIds);
            for (const id of deckIds) loadedDeckIds.delete(id);
            return {
              groups: { ...s.groups, [groupId]: { ...existing, loadedDeckIds } },
            };
          },
          false,
          { type: 'sg/markDecksUnloaded', groupId },
        ),

      removeDeck: (groupId, deckId) =>
        set(
          (s) => {
            const existing = s.groups[groupId];
            if (!existing) return s;
            const { [deckId]: _droppedCards, ...restCards } = existing.cards;
            const { [deckId]: _droppedProps, ...restProps } = existing.deckProps;
            let loadedDeckIds = existing.loadedDeckIds;
            if (loadedDeckIds.has(deckId)) {
              const next = new Set(loadedDeckIds);
              next.delete(deckId);
              loadedDeckIds = next;
            }
            return {
              groups: {
                ...s.groups,
                [groupId]: {
                  ...existing,
                  cards: restCards,
                  deckProps: restProps,
                  loadedDeckIds,
                },
              },
            };
          },
          false,
          { type: 'sg/removeDeck', groupId, deckId },
        ),

      setPendingDeckRemoval: (groupId, deckId, entry) =>
        set(
          (s) => {
            const existing = s.groups[groupId];
            // Never mint a slice for this: a slice holding only the pending
            // entry would be persisted OVER the group's offline snapshot.
            if (!existing) return s;
            const { [deckId]: _prev, ...rest } = existing.pendingRemovals ?? {};
            const pendingRemovals = entry ? { ...rest, [deckId]: entry } : rest;
            return {
              groups: { ...s.groups, [groupId]: { ...existing, pendingRemovals } },
            };
          },
          false,
          { type: 'sg/setPendingDeckRemoval', groupId, deckId },
        ),

      upsertActivity: (groupId, entry) =>
        set(
          (s) => {
            const existing = s.groups[groupId] ?? emptySlice(groupId);
            return {
              groups: {
                ...s.groups,
                [groupId]: {
                  ...existing,
                  activity: { ...existing.activity, [entry.id]: entry },
                },
              },
            };
          },
          false,
          { type: 'sg/upsertActivity', groupId, entryId: entry.id },
        ),

      upsertDeckMindmap: (groupId, entry) =>
        set(
          (s) => {
            const existing = s.groups[groupId] ?? emptySlice(groupId);
            const prev = existing.deckMindmaps?.[entry.key];
            if (prev && (prev.updatedAtISO ?? '') > (entry.updatedAtISO ?? '')) return s;
            return {
              groups: {
                ...s.groups,
                [groupId]: {
                  ...existing,
                  deckMindmaps: { ...existing.deckMindmaps, [entry.key]: entry },
                },
              },
            };
          },
          false,
          { type: 'sg/upsertDeckMindmap', groupId, key: entry.key },
        ),

      setActivity: (groupId, entries) =>
        set(
          (s) => {
            const existing = s.groups[groupId] ?? emptySlice(groupId);
            const next: Record<string, SGActivityEntry> = {};
            for (const e of entries) next[e.id] = e;
            return {
              groups: {
                ...s.groups,
                [groupId]: { ...existing, activity: next },
              },
            };
          },
          false,
          { type: 'sg/setActivity', groupId },
        ),

      setServerTs: (groupId, tsKey, ts) =>
        set(
          (s) => {
            const existing = s.groups[groupId] ?? emptySlice(groupId);
            return {
              groups: {
                ...s.groups,
                [groupId]: {
                  ...existing,
                  serverTimestamps: {
                    ...existing.serverTimestamps,
                    [tsKey]: ts,
                  },
                },
              },
            };
          },
          false,
          { type: 'sg/setServerTs', groupId, tsKey },
        ),

      setServerTsBatch: (groupId, entries) =>
        set(
          (s) => {
            const existing = s.groups[groupId] ?? emptySlice(groupId);
            return {
              groups: {
                ...s.groups,
                [groupId]: {
                  ...existing,
                  serverTimestamps: {
                    ...existing.serverTimestamps,
                    ...entries,
                  },
                },
              },
            };
          },
          false,
          { type: 'sg/setServerTsBatch', groupId },
        ),

      setLiveScore: (groupId, score) =>
        set(
          (s) => {
            const existing = s.groups[groupId] ?? emptySlice(groupId);
            return {
              groups: {
                ...s.groups,
                [groupId]: { ...existing, liveScore: score },
              },
            };
          },
          false,
          { type: 'sg/setLiveScore', groupId },
        ),
    }),
    { name: 'StudyGroupStore' },
  ),
);

// ---------------------------------------------------------------------------
// Write-through persistence — debounced, per group, split by what changed.
// ---------------------------------------------------------------------------
//
// Every mutation (broadcast merge, optimistic local write, connection-
// status change) triggers this subscription. We coalesce writes per
// groupId with a short timer so hot edits don't thrash IDB.
//
// Two tables, so a card frame does not re-encode the group:
//   - `sgGroupSnapshots`: ONE row per group with everything but `cards`
//     (meta, members, folders, deckProps, activity, timestamps …), written
//     only when one of those fields changed identity.
//   - `SG_DECK_CARDS_TABLE`: ONE row per (group, deck) holding that deck's
//     card map, written only for the LOADED decks whose bucket changed
//     identity since the last persist (the setters replace `cards[deckId]`
//     and nothing else, so identity IS the change), removed for the decks
//     that went away. A deck whose card set is still streaming in has no
//     row — so a row on disk is always a complete deck, which is what lets
//     hydrate mark every deck it reads as loaded and lets an offline open
//     trust the copy.
// `lastPersistedRef` is the diff base: what this module last wrote (or, after
// `hydrateGroupFromStorage`, what it read); its `cards` hold only the buckets
// that are on disk. On cold start we rehydrate into the same state we had
// before the app closed. Without this, opening a group on a cold client shows
// a skeleton until the WebSocket connects.

const PERSIST_DEBOUNCE_MS = 250;
const persistTimers = new Map<string, ReturnType<typeof setTimeout>>();
const lastPersistedRef = new Map<string, StudyGroupSlice | undefined>();

/** Fields kept out of the snapshot row: `cards` live in their own table,
 *  `loadedDeckIds` is derived from the rows that exist. */
const NOT_IN_SNAPSHOT: ReadonlySet<string> = new Set(['cards', 'loadedDeckIds']);

/** Identity comparison of every snapshot field (key sets included). */
function snapshotFieldsChanged(slice: StudyGroupSlice, last: StudyGroupSlice): boolean {
  const a = slice as unknown as Record<string, unknown>;
  const b = last as unknown as Record<string, unknown>;
  for (const key of Object.keys(a)) {
    if (NOT_IN_SNAPSHOT.has(key)) continue;
    if (a[key] !== b[key]) return true;
  }
  for (const key of Object.keys(b)) {
    if (!NOT_IN_SNAPSHOT.has(key) && !(key in a)) return true;
  }
  return false;
}

function persistGroup(groupId: string): void {
  const storage = getSyncStorage();
  const slice = useStudyGroupStore.getState().groups[groupId];
  const last = lastPersistedRef.get(groupId);
  if (!slice) {
    try {
      storage.removeByKey(SG_SNAPSHOTS_TABLE, groupId);
      storage.removeByIndex(SG_DECK_CARDS_TABLE, 'groupId', groupId);
    } catch (err) {
      console.error('[study-group-store] persist-delete failed:', err);
    }
    lastPersistedRef.delete(groupId);
    return;
  }
  const savedAt = new Date().toISOString();
  if (!last || snapshotFieldsChanged(slice, last)) {
    const { loadedDeckIds: _derived, ...persisted } = slice;
    const row: SGSnapshotRow = {
      groupId,
      slice: { ...persisted, cards: {} },
      savedAt,
    };
    try {
      storage.put<SGSnapshotRow>(SG_SNAPSHOTS_TABLE, row);
    } catch (err) {
      console.error('[study-group-store] persist-put failed:', err);
    }
  }
  // Card rows: loaded decks only. `persistedCards` is what the table holds
  // after this pass — the next diff base — so a bucket skipped here (still
  // streaming) is written the moment the deck is marked loaded, not lost.
  const changedDecks: StoredSGDeckCards[] = [];
  const persistedCards: StudyGroupSlice['cards'] = {};
  for (const [deckId, cards] of Object.entries(slice.cards)) {
    if (!slice.loadedDeckIds.has(deckId)) {
      // Not loaded, but still a deck of this group: a stream is re-shipping it,
      // so its bucket is partial. The row already on disk is the last COMPLETE
      // copy — keep it (and keep it as the diff base, so this pass neither
      // rewrites nor deletes it). Dropping it here is how a disconnect
      // mid-stream used to leave the member with no offline copy at all.
      const onDisk = last?.cards[deckId];
      if (onDisk) persistedCards[deckId] = onDisk;
      continue;
    }
    persistedCards[deckId] = cards;
    if (last && last.cards[deckId] === cards) continue;
    changedDecks.push({ groupId, deckId, cards, savedAt });
  }
  if (changedDecks.length > 0) safeBulkPut(storage, SG_DECK_CARDS_TABLE, changedDecks);
  if (last) {
    // Only a deck that is GONE from the group (tombstoned, or the whole deck
    // removed) loses its row — never one that is merely mid-stream.
    for (const deckId of Object.keys(last.cards)) {
      if (deckId in persistedCards) continue;
      try {
        storage.removeByKey(SG_DECK_CARDS_TABLE, [groupId, deckId]);
      } catch (err) {
        console.error('[study-group-store] persist-delete failed:', err);
      }
    }
  }
  lastPersistedRef.set(groupId, { ...slice, cards: persistedCards });
}

function schedulePersist(groupId: string): void {
  const existing = persistTimers.get(groupId);
  if (existing) clearTimeout(existing);
  const timer = setTimeout(() => {
    persistTimers.delete(groupId);
    persistGroup(groupId);
  }, PERSIST_DEBOUNCE_MS);
  persistTimers.set(groupId, timer);
}

useStudyGroupStore.subscribe((state, prev) => {
  const seen = new Set<string>();
  for (const groupId of Object.keys(state.groups)) {
    seen.add(groupId);
    if (state.groups[groupId] !== prev.groups[groupId]) {
      schedulePersist(groupId);
    }
  }
  for (const groupId of Object.keys(prev.groups)) {
    if (!seen.has(groupId)) schedulePersist(groupId);
  }
});

/**
 * Test seam: cancel every pending debounced persist and forget the diff base.
 *
 * A spec that mutates the store arms a real 250 ms timer. Left armed it
 * outlives the suite, and in a shared Jest worker it fires against the NEXT
 * suite's storage mock — a write nobody asked for. Call this in `afterEach`
 * alongside the store reset.
 */
export function _resetStudyGroupPersistence(): void {
  for (const timer of persistTimers.values()) clearTimeout(timer);
  persistTimers.clear();
  lastPersistedRef.clear();
}
